import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Navigate, useParams } from 'react-router';
import TerminalLayout from '../layout/TerminalLayout';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import ProductPickerDialog from '../stocktake/ProductPickerDialog';
import { useAuth } from '../../hooks/useAuth';
import routes from '../../routes/routes';
import { formatMoney } from '../../helpers/dataFormatter';
import { documentCardSx, terminalBarIconButtonSx } from '../../theme/terminalStyles';
import {
  useFinishIncomingAiInvoiceMutation,
  useGetIncomingAiInvoiceImagesQuery,
  useGetIncomingAiInvoiceQuery,
  useMatchIncomingAiInvoiceProductsMutation,
  useRecognizeIncomingAiInvoiceMutation,
  useReplaceIncomingAiInvoiceImagesMutation,
  useStructureIncomingAiInvoiceMutation,
  useUpdateIncomingAiInvoiceLineMutation,
} from '../../store/api/incomingAiInvoicesApi';
import {
  fileToImagePayload,
  hasIncomingAiOcr,
  incomingAiStatusColor,
  incomingAiStatusLabel,
  isIncomingAiBusy,
  unresolvedLinesCount,
} from './incomingAiInvoiceUtils';

const productTitle = (product) => {
  if (!product) return 'Товар не вибрано';
  return `${product.name || ''} ${product.modName || ''}`.trim() || `#${product.id}`;
};

const formatDuration = (value) => {
  const milliseconds = Number(value);
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return '—';
  const seconds = milliseconds / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} с`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes} хв ${remainingSeconds} с`;
};

const IncomingLineCard = ({ line, onOpen }) => {
  const hasProduct = Boolean(line.productId);
  const needsReview = !hasProduct || line.matchStatus === 'candidates';
  return (
    <Paper onClick={onOpen} sx={{ ...documentCardSx, position: 'relative', pr: 5, cursor: 'pointer' }} elevation={0}>
      <IconButton
        aria-label="відкрити"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
        sx={{ position: 'absolute', top: 8, right: 8 }}
      >
        <OpenInNewOutlinedIcon />
      </IconButton>
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        {needsReview ? <WarningAmberOutlinedIcon color="warning" /> : <CheckIcon color="success" />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Рядок {line.lineNo || line.id} · {line.article || line.barcode || 'без коду'}
          </Typography>
          <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
            {line.correctedName || line.recognizedName || productTitle(line.product)}
          </Typography>
          <Typography variant="body2" color={hasProduct ? 'text.secondary' : 'warning.main'} sx={{ mt: 0.5 }}>
            {productTitle(line.product)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            К-сть: <strong>{line.quantity}</strong> {line.unit || ''} · Ціна: <strong>{formatMoney(line.price ?? 0)}</strong> · Сума:{' '}
            <strong>{formatMoney(line.sum ?? 0)}</strong>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const IncomingAiInvoicePage = () => {
  const { id } = useParams();
  const { authState } = useAuth();
  const user = authState.user;
  const [tab, setTab] = useState('products');
  const [activeLine, setActiveLine] = useState(null);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [recognizeError, setRecognizeError] = useState('');
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const [operationsAnchor, setOperationsAnchor] = useState(null);
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceFiles, setReplaceFiles] = useState([]);
  const replaceInputRef = useRef(null);
  const { data: doc, isFetching, error } = useGetIncomingAiInvoiceQuery(id, {
    skip: !id,
    pollingInterval: pollingEnabled ? 3000 : 0,
    refetchOnMountOrArgChange: true,
  });
  const { data: images = [], isFetching: isFetchingImages } = useGetIncomingAiInvoiceImagesQuery(id, {
    skip: tab !== 'images' && !replaceOpen,
  });
  const [updateLine, { isLoading: isUpdating }] = useUpdateIncomingAiInvoiceLineMutation();
  const [finishInvoice, { isLoading: isFinishing }] = useFinishIncomingAiInvoiceMutation();
  const [recognizeInvoice, { isLoading: isRecognizing }] = useRecognizeIncomingAiInvoiceMutation();
  const [replaceImages, { isLoading: isReplacing }] = useReplaceIncomingAiInvoiceImagesMutation();
  const [structureInvoice, { isLoading: isStructuring }] = useStructureIncomingAiInvoiceMutation();
  const [matchProducts, { isLoading: isMatching }] = useMatchIncomingAiInvoiceProductsMutation();

  const unresolved = unresolvedLinesCount(doc);
  const visibleProducts = useMemo(() => {
    const products = doc?.products || [];
    return unmatchedOnly ? products.filter((line) => !line.productId) : products;
  }, [doc, unmatchedOnly]);
  const isDocBusy = isIncomingAiBusy(doc);
  const isOperationPending = isRecognizing || isReplacing || isStructuring || isMatching || isDocBusy;
  const operationsLocked = !doc || doc.isDone || doc.sentToERP || isOperationPending;
  const hasOcr = hasIncomingAiOcr(doc);
  const hasProductLines = Boolean((doc?.products || []).length);
  const hasImages = Boolean((doc?.images || []).length || images.length);
  const showLogs = Boolean(user?.terminalWorkplace?.showLogs);
  const ocrText = doc?.aiRawData?.ocr?.text || (doc?.aiRawData?.ocr?.pages || []).map((page) => page.text).join('\n\n');
  const llmData = useMemo(() => {
    if (!doc?.aiRawData) return null;
    const { ocr, ...rest } = doc.aiRawData;
    return Object.keys(rest).length ? rest : null;
  }, [doc]);
  const aiJsonText = llmData ? JSON.stringify(llmData, null, 2) : '';
  const ocrRawText = doc?.aiRawData?.ocr ? JSON.stringify(doc.aiRawData.ocr, null, 2) : '';
  const timings = {
    ...(doc?.aiRawData?.ocr?.timings || {}),
    ...(doc?.aiRawData?.timings || {}),
  };
  const timingRows = [
    ['OCR · повний аркуш', timings.ocrMainMs],
    ['OCR · назви товарів', timings.ocrNamesMs],
    ['OCR · числові колонки', timings.ocrNumbersMs],
    ['OCR · загалом', timings.ocrTotalMs],
    ['LLM · структуризація', timings.llmMs],
    ['OCR + LLM · загалом', timings.totalMs],
  ].filter(([, value]) => Number.isFinite(Number(value)) && Number(value) > 0);

  useEffect(() => {
    if (error) {
      setPollingEnabled(false);
      return;
    }
    if (isIncomingAiBusy(doc)) {
      setPollingEnabled(true);
      return;
    }
    setPollingEnabled(false);
    if (!unresolvedLinesCount(doc)) setUnmatchedOnly(false);
  }, [doc, error]);
  const lineDraft = useMemo(() => ({
    correctedName: activeLine?.correctedName || activeLine?.recognizedName || '',
    barcode: activeLine?.barcode || '',
    article: activeLine?.article || '',
    weightCode: activeLine?.weightCode || '',
    unit: activeLine?.unit || '',
    quantity: activeLine?.quantity ?? 0,
    price: activeLine?.price ?? 0,
    sum: activeLine?.sum ?? 0,
  }), [activeLine]);
  const [editValues, setEditValues] = useState(lineDraft);

  if (!user?.terminalWorkplace?.isIncomingInvoicesAi) {
    return <Navigate to={routes.root} replace />;
  }

  const showUnmatched = () => {
    setTab('products');
    setUnmatchedOnly((applied) => !applied);
  };

  const openLine = (line) => {
    setCatalogOpen(false);
    setActiveLine(line);
    setEditValues({
      correctedName: line.correctedName || line.recognizedName || '',
      barcode: line.barcode || '',
      article: line.article || '',
      weightCode: line.weightCode || '',
      unit: line.unit || '',
      quantity: line.quantity ?? 0,
      price: line.price ?? 0,
      sum: line.sum ?? 0,
    });
  };

  const saveLine = async (extra = {}) => {
    if (!activeLine) return;
    const updated = await updateLine({
      id,
      lineId: activeLine.id,
      ...editValues,
      ...extra,
    }).unwrap();
    const nextLine = (updated.products || []).find((line) => line.id === activeLine.id);
    if (nextLine) setActiveLine(nextLine);
  };

  const handleFinish = async () => {
    await finishInvoice(id).unwrap();
    setConfirmFinish(false);
  };

  const runOperation = async (action) => {
    setRecognizeError('');
    setOperationsAnchor(null);
    setPollingEnabled(true);
    try {
      await action();
    } catch (err) {
      setRecognizeError(err?.data?.message || err?.message || 'Не вдалося виконати операцію');
    }
  };

  const handleRecognize = () => runOperation(() => recognizeInvoice(id).unwrap());
  const handleStructure = () => runOperation(() => structureInvoice(id).unwrap());
  const handleMatchProducts = () => runOperation(() => matchProducts(id).unwrap());

  const handleReplaceImages = async () => {
    setRecognizeError('');
    try {
      const nextImages = await Promise.all(replaceFiles.map(fileToImagePayload));
      await replaceImages({ id, images: nextImages }).unwrap();
      setReplaceOpen(false);
      setReplaceFiles([]);
    } catch (err) {
      setRecognizeError(err?.data?.message || err?.message || 'Не вдалося замінити зображення');
    }
  };

  const bottomBar = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        <IconButton
          aria-label="операції"
          disabled={operationsLocked}
          onClick={(event) => setOperationsAnchor(event.currentTarget)}
          sx={terminalBarIconButtonSx}
        >
          <AutoAwesomeOutlinedIcon />
        </IconButton>
        <IconButton
          aria-label="готово"
          disabled={!doc || doc.sentToERP || isFinishing || isOperationPending}
          onClick={() => setConfirmFinish(true)}
          sx={terminalBarIconButtonSx}
        >
          <CheckIcon />
        </IconButton>
      </Box>
      <Typography variant="body2" sx={{ alignSelf: 'center', fontWeight: 600 }}>
        {isOperationPending ? incomingAiStatusLabel(doc) || 'AI обробляє...' : doc ? incomingAiStatusLabel(doc) : ''}
      </Typography>
    </Box>
  );

  return (
    <TerminalLayout
      title="AI накладна"
      titleIcon={<ReceiptLongOutlinedIcon />}
      backTo={routes.incomingAiInvoices}
      bottomBar={bottomBar}
    >
      {!doc && isFetching ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box> : null}
      {error ? <Alert severity="error">{error?.data?.message || 'Помилка завантаження'}</Alert> : null}
      {recognizeError ? <Alert severity="error" sx={{ mb: 1 }}>{recognizeError}</Alert> : null}
      {doc ? (
        <>
          <Paper sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }} elevation={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" noWrap>№ {doc.number || doc.extCode || doc.id}</Typography>
                <Typography variant="body2" color="text.secondary" noWrap>{doc.supplierName || doc.clientName || '—'}</Typography>
              </Box>
              <Box aria-hidden sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: incomingAiStatusColor(doc), flexShrink: 0 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Chip size="small" label={incomingAiStatusLabel(doc)} />
              {unresolved > 0 ? (
                <Chip
                  size="small"
                  color="warning"
                  clickable
                  variant={unmatchedOnly ? 'filled' : 'outlined'}
                  label={`Не зіставлено: ${unresolved}`}
                  onClick={showUnmatched}
                />
              ) : null}
              <Chip size="small" label={`${(doc.products || []).length} рядків`} />
              {isDocBusy ? <Chip size="small" color="info" label={incomingAiStatusLabel(doc)} /> : null}
            </Box>
            {!doc.isDone && !doc.sentToERP ? (
              <>
                <TerminalOrangeButton
                  disabled={operationsLocked}
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={(event) => setOperationsAnchor(event.currentTarget)}
                  sx={{ mt: 1.25 }}
                >
                  Операції:
                </TerminalOrangeButton>
                <Menu
                  anchorEl={operationsAnchor}
                  open={Boolean(operationsAnchor)}
                  onClose={() => setOperationsAnchor(null)}
                >
                  <MenuItem disabled={operationsLocked || !hasImages} onClick={handleRecognize}>
                    Розпізнати та знайти товари
                  </MenuItem>
                  <MenuItem
                    disabled={operationsLocked}
                    onClick={() => {
                      setOperationsAnchor(null);
                      setReplaceOpen(true);
                    }}
                  >
                    Замінити зображення
                  </MenuItem>
                  <MenuItem disabled={operationsLocked || !hasOcr} onClick={handleStructure}>
                    Сформувати JSON, знайти товари
                  </MenuItem>
                  <MenuItem disabled={operationsLocked || !hasProductLines} onClick={handleMatchProducts}>
                    Знайти товари
                  </MenuItem>
                </Menu>
              </>
            ) : null}
            {doc.recognizeError ? <Alert severity="error" sx={{ mt: 1 }}>{doc.recognizeError}</Alert> : null}
          </Paper>

          <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{ mb: 1 }}>
            <Tab value="products" label="Товари" />
            <Tab value="header" label="Шапка" />
            <Tab value="images" label="Фото" />
            {showLogs ? <Tab value="logs" label="Логи" /> : null}
          </Tabs>

          {tab === 'products' ? (
            <>
              {unmatchedOnly ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" color="warning.main" fontWeight={600}>
                    Незіставлені рядки
                  </Typography>
                  <Chip size="small" label="Усі товари" clickable onClick={() => setUnmatchedOnly(false)} />
                </Box>
              ) : null}
              {visibleProducts.map((line) => (
                <IncomingLineCard key={line.id} line={line} onOpen={() => openLine(line)} />
              ))}
              {!visibleProducts.length ? (
                <Alert severity="info">
                  {unmatchedOnly ? 'Незіставлених рядків немає.' : 'Рядків товарів ще немає.'}
                </Alert>
              ) : null}
            </>
          ) : tab === 'header' ? (
            <Paper sx={{ ...documentCardSx, mb: 0 }} elevation={0}>
              <Typography variant="body2">Дата документа: <strong>{doc.documentDate ? new Date(doc.documentDate).toLocaleDateString('uk-UA') : '—'}</strong></Typography>
              <Typography variant="body2">Постачальник: <strong>{doc.supplierName || '—'}</strong></Typography>
              <Typography variant="body2">ІПН постачальника: <strong>{doc.supplierTaxId || '—'}</strong></Typography>
              <Typography variant="body2">Клієнт: <strong>{doc.clientName || '—'}</strong></Typography>
              <Typography variant="body2">ІПН клієнта: <strong>{doc.clientTaxId || '—'}</strong></Typography>
              <Typography variant="body2">Сума: <strong>{formatMoney(doc.sum ?? 0)} ₴</strong></Typography>
            </Paper>
          ) : tab === 'images' ? (
            <Box>
              {isFetchingImages ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box> : null}
              {images.map((image) => (
                <Paper key={image.id} sx={documentCardSx} elevation={0}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Сторінка {image.pageNo} · {image.filename}
                  </Typography>
                  <Box
                    component="img"
                    alt={`Сторінка ${image.pageNo}`}
                    src={`data:${image.mimeType || 'image/jpeg'};base64,${image.imageData}`}
                    sx={{ width: '100%', borderRadius: 1, display: 'block' }}
                  />
                </Paper>
              ))}
              {!isFetchingImages && images.length === 0 ? <Alert severity="info">Фото документа не знайдено.</Alert> : null}
            </Box>
          ) : showLogs ? (
            <Box>
              {timingRows.length ? (
                <Paper sx={documentCardSx} elevation={0}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Час обробки
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 0.75 }}>
                    {timingRows.map(([label, value]) => (
                      <React.Fragment key={label}>
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" fontWeight={700}>{formatDuration(value)}</Typography>
                      </React.Fragment>
                    ))}
                  </Box>
                </Paper>
              ) : null}
              <Paper sx={documentCardSx} elevation={0}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  OCR текст
                </Typography>
                <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, fontSize: 12 }}>
                  {ocrText || 'OCR текст ще не записано.'}
                </Box>
              </Paper>
              <Paper sx={documentCardSx} elevation={0}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  JSON від LLM
                </Typography>
                <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, fontSize: 12 }}>
                  {aiJsonText || 'JSON ще не сформовано.'}
                </Box>
              </Paper>
              <Paper sx={documentCardSx} elevation={0}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  OCR raw
                </Typography>
                <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, fontSize: 12 }}>
                  {ocrRawText || 'Сирі дані OCR ще не записано.'}
                </Box>
              </Paper>
            </Box>
          ) : null}
        </>
      ) : null}

      <Dialog open={Boolean(activeLine)} onClose={() => setActiveLine(null)} fullScreen>
        <DialogTitle>Рядок накладної</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 1.25, mt: 1 }}>
            <TextField label="Назва" value={editValues.correctedName || ''} onChange={(e) => setEditValues((s) => ({ ...s, correctedName: e.target.value }))} fullWidth />
            <TextField label="Штрихкод" value={editValues.barcode || ''} onChange={(e) => setEditValues((s) => ({ ...s, barcode: e.target.value }))} fullWidth />
            <TextField label="Артикул" value={editValues.article || ''} onChange={(e) => setEditValues((s) => ({ ...s, article: e.target.value }))} fullWidth />
            <TextField label="Ваговий код" value={editValues.weightCode || ''} onChange={(e) => setEditValues((s) => ({ ...s, weightCode: e.target.value }))} fullWidth />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <TextField label="Кількість" type="number" value={editValues.quantity} onChange={(e) => setEditValues((s) => ({ ...s, quantity: e.target.value }))} />
              <TextField label="Од." value={editValues.unit || ''} onChange={(e) => setEditValues((s) => ({ ...s, unit: e.target.value }))} />
              <TextField label="Ціна" type="number" value={editValues.price} onChange={(e) => setEditValues((s) => ({ ...s, price: e.target.value }))} />
              <TextField label="Сума" type="number" value={editValues.sum} onChange={(e) => setEditValues((s) => ({ ...s, sum: e.target.value }))} />
            </Box>
          </Box>

          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
            Варіанти товарів
          </Typography>
          <TerminalOrangeButton sx={{ mt: 1 }} disabled={isUpdating} onClick={() => setCatalogOpen(true)}>
            Вибрати з каталогу
          </TerminalOrangeButton>
          {activeLine?.product ? (
            <Alert severity="success" sx={{ mt: 1 }}>Вибрано: {productTitle(activeLine.product)}</Alert>
          ) : null}
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Товар</TableCell>
                <TableCell>Score</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(activeLine?.candidates || []).map((candidate) => (
                <TableRow key={candidate.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{productTitle(candidate)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Арт: {candidate.article || '—'} · Код: {candidate.code || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{candidate.score}</TableCell>
                  <TableCell align="right">
                    <TerminalOrangeButton size="small" disabled={isUpdating} onClick={() => saveLine({ productId: candidate.id })}>
                      Вибрати
                    </TerminalOrangeButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!activeLine?.candidates?.length ? <Alert severity="info" sx={{ mt: 1 }}>Кандидатів не знайдено.</Alert> : null}
        </DialogContent>
        <DialogActions>
          <TerminalOrangeButton onClick={() => saveLine({ rematch: true })} disabled={isUpdating}>
            Знайти знову
          </TerminalOrangeButton>
          <TerminalOrangeButton onClick={() => setActiveLine(null)} disabled={isUpdating}>
            Закрити
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <ProductPickerDialog
        open={catalogOpen}
        standalone
        title="Вибрати відповідність"
        initialSearch={activeLine?.correctedName || activeLine?.recognizedName || ''}
        onClose={() => setCatalogOpen(false)}
        onSelectProduct={async (product) => {
          setCatalogOpen(false);
          try {
            await saveLine({ productId: product.id });
          } catch (err) {
            setRecognizeError(err?.data?.message || err?.message || 'Не вдалося зіставити товар');
          }
        }}
      />

      <Dialog open={replaceOpen} onClose={() => (isReplacing ? null : setReplaceOpen(false))} fullWidth maxWidth="sm">
        <DialogTitle>Замінити зображення</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Вибрані фото замінять усі поточні зображення документа. Розпізнавання після заміни не запускається.
          </Typography>
          <input
            ref={replaceInputRef}
            hidden
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={(e) => setReplaceFiles([...e.target.files])}
          />
          <TerminalOrangeButton onClick={() => replaceInputRef.current?.click()} disabled={isReplacing}>
            Вибрати або сфотографувати
          </TerminalOrangeButton>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Фото: <strong>{replaceFiles.length}</strong>
          </Typography>
          {replaceFiles.map((file) => (
            <Typography key={`${file.name}-${file.size}`} variant="body2" color="text.secondary" noWrap>
              {file.name}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <TerminalOrangeButton onClick={() => setReplaceOpen(false)} disabled={isReplacing}>
            Скасувати
          </TerminalOrangeButton>
          <TerminalOrangeButton onClick={handleReplaceImages} disabled={isReplacing || replaceFiles.length === 0}>
            Замінити
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmFinish} onClose={() => setConfirmFinish(false)}>
        <DialogTitle>Підтвердити готовність?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Після підтвердження накладна буде готова для забору основною системою по API.
          </Typography>
          {unresolved > 0 ? <Alert severity="warning" sx={{ mt: 1 }}>Є незіставлені товари: {unresolved}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <TerminalOrangeButton onClick={() => setConfirmFinish(false)} disabled={isFinishing}>
            Скасувати
          </TerminalOrangeButton>
          <TerminalOrangeButton onClick={handleFinish} disabled={isFinishing || unresolved > 0}>
            OK
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>
    </TerminalLayout>
  );
};

export default IncomingAiInvoicePage;
